/** Which video URLs become players, shared by the public page and the editor.
 *
 * An allowlist rather than "embed any URL": an ad body is user input rendered
 * on a public page, so the set of third-party iframes it can produce has to be
 * closed. Adding a provider means adding a row here — both sides pick it up.
 *
 * A clip the recruiter uploaded is the one source that needs no provider: it
 * is served from our own route, so it plays in a <video> element and no
 * third-party script is involved at all.
 */

import { isUploadedVideoSrc } from "./uploaded-media";

export interface VideoProvider {
	/** Shown in the editor when a pasted link is not recognised. */
	readonly label: string;
	/** Must anchor both ends: the whole line is the URL or it is not a video. */
	readonly pattern: RegExp;
	readonly embedUrl: (id: string) => string;
}

export const VIDEO_PROVIDERS: readonly VideoProvider[] = [
	{
		label: "youtube.com/watch, youtu.be, youtube.com/shorts",
		pattern:
			/^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([\w-]{11})\S*$/,
		embedUrl: (id) => `https://www.youtube-nocookie.com/embed/${id}`,
	},
	{
		label: "vimeo.com",
		pattern: /^(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(\d{6,})\S*$/,
		embedUrl: (id) => `https://player.vimeo.com/video/${id}`,
	},
	{
		label: "loom.com/share",
		pattern: /^(?:https?:\/\/)?(?:www\.)?loom\.com\/share\/([0-9a-f]{32})\S*$/,
		embedUrl: (id) => `https://www.loom.com/embed/${id}`,
	},
];

/** Either a provider's player or a file the recruiter uploaded into the ad.
 *
 * They differ in what the page emits — a third-party iframe against a plain
 * <video> served from our own route — so the distinction is carried rather
 * than flattened to a URL.
 */
export type VideoMatch =
	| { readonly kind: "provider"; readonly embedSrc: string }
	| { readonly kind: "file"; readonly src: string };

/** A line that is nothing but a supported video URL. */
export function matchVideoLine(line: string): VideoMatch | null {
	for (const provider of VIDEO_PROVIDERS) {
		const match = line.match(provider.pattern);
		if (match) return { kind: "provider", embedSrc: provider.embedUrl(match[1]) };
	}
	if (isUploadedVideoSrc(line)) return { kind: "file", src: line };
	return null;
}

/** The player markup, identical wherever it is rendered. */
export function videoEmbedHtml(match: VideoMatch): string {
	if (match.kind === "file") {
		// preload="metadata" so a long clip costs the visitor its first frame
		// and its duration, not the whole file, before they press play.
		return `<div class="video-embed"><video src="${match.src}" controls preload="metadata" playsinline></video></div>`;
	}
	return `<div class="video-embed"><iframe src="${match.embedSrc}" title="Video" loading="lazy" allowfullscreen allow="encrypted-media; picture-in-picture"></iframe></div>`;
}

/** "youtube.com/watch, youtu.be, …, vimeo.com or loom.com/share" */
export function supportedVideoHint(): string {
	const labels = VIDEO_PROVIDERS.map((provider) => provider.label);
	return `${labels.slice(0, -1).join(", ")} or ${labels[labels.length - 1]}`;
}
